import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { TableDataResponse } from 'types/general';
import {
  ITransaction,
  IBaseTransmissionAct,
  ITransmissionAct,
  IDeposit,
} from 'types/income';
import { exportPdfConfig, exportXlsxConfig, getSorting } from 'utils/general';
import {
  DepositsFilters,
  TransactionsFilters,
  TransmissionActsFilters,
} from './transmission-acts.types';

const getFilters = (filters: Partial<TransmissionActsFilters>) => ({
  page: filters.page,
  pageSize: filters.pageSize,
  ...(filters.sort && ['date', 'company'].includes(filters.sort.columnKey)
    ? getSorting(filters.sort)
    : {}),
  filter: {
    companyIds: filters.company ? [filters.company] : undefined,
    dateFrom: filters.date?.from,
    dateTo: filters.date?.to,
  },
});

const getTransactionsFilters = (filters: Partial<TransactionsFilters>) => ({
  filter: {
    companyIds: filters.company ? [filters.company] : undefined,
    dateFrom: filters.date?.from,
    dateTo: filters.date?.to,
  },
});

const getDepositsFilters = (filters: Partial<DepositsFilters>) => ({
  page: filters.page,
  pageSize: filters.pageSize,
  ...(filters.sort &&
  ['creationDate', 'amount', 'accountNumber'].includes(filters.sort.columnKey)
    ? getSorting(filters.sort)
    : {}),
  filter: {
    companyIds: filters.company ? [filters.company] : undefined,
    dateFrom: filters.date?.from,
    dateTo: filters.date?.to,
  },
});

interface GetTransmissionActs {
  filters: Partial<TransmissionActsFilters>;
}

interface GetTransmissionAct {
  transmissionActId: string;
}

interface GetTransactions {
  filters: Partial<TransactionsFilters>;
}

interface GetDeposits {
  filters: Partial<DepositsFilters>;
}

interface DownloadPdf {
  transmissionActId: string;
}

interface ExportXls {
  params: Partial<{
    transmissionActId: string;
    ongoingBalance: boolean;
    companyId: string;
  }>;
}

export const loaders = {
  getTransmissionActs: ({ filters }: GetTransmissionActs) => {
    return api<TableDataResponse<IBaseTransmissionAct>>({
      url: Endpoints.clientAdmin.transmissionActs.root,
      params: getFilters(filters),
    });
  },
  getTransmissionAct: ({ transmissionActId }: GetTransmissionAct) => {
    return api<ITransmissionAct>({
      url: Endpoints.clientAdmin.transmissionActs.byId.root.replace(
        ':transmissionActId',
        transmissionActId,
      ),
    });
  },
  getTransactions: ({ filters }: GetTransactions) => {
    return api<TableDataResponse<ITransaction>>({
      url: Endpoints.clientAdmin.transmissionActs.balance,
      params: getTransactionsFilters(filters),
    });
  },
  getDeposits: ({ filters }: GetDeposits) => {
    return api<TableDataResponse<IDeposit>>({
      url: Endpoints.clientAdmin.transmissionActs.deposits,
      params: getDepositsFilters(filters),
    });
  },
  downloadPdf: ({ transmissionActId }: DownloadPdf) => {
    return api({
      url: Endpoints.melpAdmin.transmissionActs.byId.export.pdf.replace(
        ':transmissionActId',
        transmissionActId,
      ),
      ...exportPdfConfig,
    });
  },
  exportXls: ({ params }: ExportXls) => {
    return api({
      url: Endpoints.clientAdmin.transmissionActs.export.xls,
      params,
      ...exportXlsxConfig,
    });
  },
};
